import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Modal,
  Table,
  Badge,
  Alert,
  Form,
  Spinner,
} from "react-bootstrap";
import {
  FaShieldAlt,
  FaEye,
  FaCheck,
  FaTimes,
  FaFileAlt,
  FaUser,
  FaBuilding,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const KYCVerification = () => {
  const [kycRequests, setKycRequests] = useState([]);
  const [statusCounts, setStatusCounts] = useState({
    pending: 0,
    verified: 0,
    rejected: 0,
  });
  const [selectedKyc, setSelectedKyc] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(""); // 'approve' or 'reject'
  const [rejectionReason, setRejectionReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all"); // Changed default to 'all'
  const navigate = useNavigate();

  useEffect(() => {
    fetchStatusCounts();
  }, []);

  useEffect(() => {
    fetchKYCRequests();
  }, [filterStatus]);

  const fetchStatusCounts = async () => {
    try {
      const bases = Array.from(
        new Set(
          [process.env.REACT_APP_API_BASE_URL, window.location.origin].filter(
            Boolean,
          ),
        ),
      );

      let counts = { pending: 0, verified: 0, rejected: 0 };
      let lastErr = null;

      for (const base of bases) {
        try {
          const adminToken =
            localStorage.getItem("adminToken") ||
            sessionStorage.getItem("adminToken");
          const headers = {
            "Content-Type": "application/json",
          };
          if (adminToken) {
            headers["Authorization"] = `Bearer ${adminToken}`;
          }

          // Fetch counts for each status
          const statuses = ["pending", "verified", "rejected"];
          const promises = statuses.map((status) =>
            fetch(`${base}/api/kyc/all?status=${status}&limit=1000`, {
              headers,
            })
              .then((res) => {
                if (res.ok) {
                  return res.json();
                } else {
                  throw new Error(`Failed to fetch ${status} KYC requests`);
                }
              })
              .then((data) => ({
                status,
                count: Array.isArray(data.kycRecords)
                  ? data.kycRecords.length
                  : 0,
              }))
              .catch(() => ({ status, count: 0 })),
          );

          const results = await Promise.all(promises);

          results.forEach((result) => {
            counts[result.status] = result.count;
          });

          break;
        } catch (e) {
          lastErr = e;
          continue;
        }
      }

      setStatusCounts(counts);
    } catch (error) {
      console.error("Error fetching status counts:", error);
    }
  };

  const fetchKYCRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      const bases = Array.from(
        new Set(
          [
            process.env.REACT_APP_API_BASE_URL,
            "https://api.quvikme.com",
          ].filter(Boolean),
        ),
      );

      let kycData = null;
      let lastErr = null;

      for (const base of bases) {
        try {
          const adminToken =
            localStorage.getItem("adminToken") ||
            sessionStorage.getItem("adminToken");
          const headers = {
            "Content-Type": "application/json",
          };
          if (adminToken) {
            headers["Authorization"] = `Bearer ${adminToken}`;
          }

          // Modified to handle 'all' filter status
          const url =
            filterStatus === "all"
              ? `${base}/api/kyc/all?limit=100`
              : `${base}/api/kyc/all?status=${filterStatus}&limit=100`;

          const kycRes = await fetch(url, { headers });

          if (!kycRes.ok) {
            lastErr = new Error(`Failed to fetch from ${base}`);
            continue;
          }

          kycData = await kycRes.json();
          break;
        } catch (e) {
          lastErr = e;
          continue;
        }
      }

      if (!kycData) {
        throw lastErr || new Error("Failed to load KYC data");
      }

      // The KYC data now includes vendor information directly
      const kycArray = Array.isArray(kycData.kycRecords)
        ? kycData.kycRecords
        : [];
      setKycRequests(kycArray);
    } catch (error) {
      console.error("Error fetching KYC requests:", error);
      setError("Failed to load KYC verification requests");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (kycRequest) => {
    setSelectedKyc(kycRequest);
    setShowDetailModal(true);
  };

  const handleAction = (kycRequest, action) => {
    setSelectedKyc(kycRequest);
    setActionType(action);
    setRejectionReason("");
    setShowActionModal(true);
  };

  const processAction = async () => {
    if (!selectedKyc) return;

    if (actionType === "reject" && !rejectionReason.trim()) {
      alert("Please provide a rejection reason");
      return;
    }

    try {
      setActionLoading(true);

      const bases = Array.from(
        new Set(
          [
            process.env.REACT_APP_API_BASE_URL,
            "https://api.quvikme.com",
          ].filter(Boolean),
        ),
      );

      let success = false;
      let lastErr = null;

      for (const base of bases) {
        try {
          const adminToken =
            localStorage.getItem("adminToken") ||
            sessionStorage.getItem("adminToken");
          const endpoint =
            actionType === "approve"
              ? `${base}/api/kyc/${selectedKyc.vendorId}/verify`
              : `${base}/api/kyc/${selectedKyc.vendorId}/reject`;

          const body =
            actionType === "approve"
              ? { adminId: adminToken || "admin" }
              : {
                  rejectionReason: rejectionReason.trim(),
                  adminId: adminToken || "admin",
                };

          const headers = {
            "Content-Type": "application/json",
          };
          if (adminToken) {
            headers["Authorization"] = `Bearer ${adminToken}`;
          }

          const response = await fetch(endpoint, {
            method: "PUT",
            headers,
            body: JSON.stringify(body),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || `Failed to ${actionType} KYC`);
          }

          success = true;
          break;
        } catch (e) {
          lastErr = e;
          continue;
        }
      }

      if (!success) {
        throw lastErr || new Error(`Failed to ${actionType} KYC`);
      }

      // Refresh the list and counts
      await fetchKYCRequests();
      await fetchStatusCounts(); // Refresh counts after action

      setShowActionModal(false);
      setSelectedKyc(null);

      alert(
        `KYC ${actionType === "approve" ? "approved" : "rejected"} successfully`,
      );
    } catch (error) {
      console.error(`Error ${actionType}ing KYC:`, error);
      alert(`Failed to ${actionType} KYC: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: "warning", icon: FaFileAlt, text: "Pending Review" },
      verified: { variant: "success", icon: FaCheck, text: "Verified" },
      rejected: { variant: "danger", icon: FaTimes, text: "Rejected" },
      not_submitted: {
        variant: "secondary",
        icon: FaFileAlt,
        text: "Not Submitted",
      },
    };

    const config = statusConfig[status] || statusConfig.not_submitted;
    const IconComponent = config.icon;

    return (
      <Badge bg={config.variant} className="d-flex align-items-center gap-1">
        <IconComponent size={12} />
        {config.text}
      </Badge>
    );
  };

  const openDocument = (url) => {
    if (url) {
      window.open(url, "_blank");
    }
  };

  if (loading && kycRequests.length === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <Spinner animation="border" variant="primary" />
        <span className="ms-2">Loading KYC requests...</span>
      </div>
    );
  }

  if (error && kycRequests.length === 0) {
    return (
      <Alert variant="danger" className="m-3">
        <Alert.Heading>Error</Alert.Heading>
        <p>{error}</p>
        <Button variant="outline-danger" onClick={fetchKYCRequests}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <div className="kyc-verification-container p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="mb-0">
          <FaShieldAlt className="me-2 text-primary" />
          KYC Verification Requests
        </h1>
        <Button variant="outline-secondary" onClick={() => navigate("/")}>
          Back to Dashboard
        </Button>
      </div>

      {/* Filter Tabs */}
      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex gap-2">
            {["all", "pending", "verified", "rejected"].map((status) => (
              <Button
                key={status}
                variant={
                  filterStatus === status ? "primary" : "outline-primary"
                }
                size="sm"
                onClick={() => setFilterStatus(status)}
              >
                {status === "all"
                  ? "All"
                  : status.charAt(0).toUpperCase() + status.slice(1)}
                <Badge bg="light" text="dark" className="ms-2">
                  {status === "all"
                    ? statusCounts.pending +
                      statusCounts.verified +
                      statusCounts.rejected
                    : statusCounts[status]}
                </Badge>
              </Button>
            ))}
          </div>
        </Card.Header>
      </Card>

      {/* KYC Requests Table */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">
            {filterStatus === "all"
              ? "All"
              : filterStatus.charAt(0).toUpperCase() +
                filterStatus.slice(1)}{" "}
            KYC Requests
          </h5>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" variant="primary" />
              <span className="ms-2">
                Loading {filterStatus} KYC requests...
              </span>
            </div>
          ) : kycRequests.length === 0 ? (
            <div className="text-center py-5">
              <FaFileAlt size={48} className="text-muted mb-3" />
              <h5 className="text-muted">
                No {filterStatus} KYC requests found
              </h5>
              <p className="text-muted">
                There are currently no KYC requests with {filterStatus} status.
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Vendor</th>
                    <th>Contact</th>
                    <th>Business</th>
                    <th>Status</th>
                    <th>Payment Method</th>
                    <th>Submitted</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {kycRequests.map((kyc) => (
                    <tr key={kyc.vendorId}>
                      <td>
                        <div className="d-flex align-items-center">
                          <FaUser className="text-muted me-2" />
                          <div>
                            <div className="fw-semibold">
                              {kyc.vendorInfo?.name || "Unknown Vendor"}
                            </div>
                            <small className="text-muted">
                              ID: {kyc.vendorId.substring(0, 8)}...
                            </small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div>{kyc.vendorInfo?.email}</div>
                          <small className="text-muted">
                            {kyc.vendorInfo?.phone}
                          </small>
                        </div>
                      </td>
                      <td>
                        <Badge bg="info" className="text-dark">
                          {kyc.vendorInfo?.businessCategory}
                        </Badge>
                      </td>
                      <td>{getStatusBadge(kyc.status)}</td>
                      <td>
                        {kyc.bankDetails && kyc.bankDetails.accountNumber ? (
                          <Badge bg="primary">Bank</Badge>
                        ) : kyc.upiDetails && kyc.upiDetails.upiId ? (
                          <Badge bg="success">UPI</Badge>
                        ) : (
                          <Badge bg="secondary">None</Badge>
                        )}
                      </td>
                      <td>
                        <small>
                          {kyc.submittedAt
                            ? new Date(kyc.submittedAt).toLocaleDateString()
                            : "N/A"}
                        </small>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleViewDetails(kyc)}
                          >
                            <FaEye size={12} className="me-1" />
                            View
                          </Button>
                          {kyc.status === "pending" && (
                            <>
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => handleAction(kyc, "approve")}
                              >
                                <FaCheck size={12} className="me-1" />
                                Approve
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleAction(kyc, "reject")}
                              >
                                <FaTimes size={12} className="me-1" />
                                Reject
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Detail Modal */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaShieldAlt className="me-2" />
            KYC Details - {selectedKyc?.vendorInfo?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedKyc && (
            <Row>
              <Col md={8}>
                <Card className="mb-3">
                  <Card.Header>
                    <FaUser className="me-2" />
                    Vendor Information
                  </Card.Header>
                  <Card.Body>
                    <p>
                      <strong>Name:</strong> {selectedKyc.vendorInfo?.name}
                    </p>
                    <p>
                      <strong>Email:</strong> {selectedKyc.vendorInfo?.email}
                    </p>
                    <p>
                      <strong>Phone:</strong> {selectedKyc.vendorInfo?.phone}
                    </p>
                    <p>
                      <strong>Business:</strong>{" "}
                      {selectedKyc.vendorInfo?.businessCategory}
                    </p>
                    <p>
                      <strong>Status:</strong>{" "}
                      {getStatusBadge(selectedKyc.status)}
                    </p>
                  </Card.Body>
                </Card>

                <Card>
                  <Card.Header>
                    <FaBuilding className="me-2" />
                    KYC - Payment Details
                  </Card.Header>
                  <Card.Body>
                    {/* Bank Details Section */}
                    <h6 className="mb-3">Bank Details</h6>
                    {selectedKyc.bankDetails &&
                    selectedKyc.bankDetails.accountNumber ? (
                      <>
                        <p>
                          <strong>Account Number:</strong>
                          {selectedKyc.bankDetails.accountNumber}
                        </p>
                        <p>
                          <strong>IFSC Code:</strong>{" "}
                          {selectedKyc.bankDetails.ifscCode}
                        </p>
                        <p>
                          <strong>Account Holder:</strong>{" "}
                          {selectedKyc.bankDetails.accountHolderName}
                        </p>
                        <p>
                          <strong>Bank Name:</strong>{" "}
                          {selectedKyc.bankDetails.bankName}
                        </p>
                        {/* Cancelled Cheque Image */}
                        {selectedKyc.bankDetails?.cancelledChequeUrl && (
                          <div className="mt-3">
                            <strong>Cancelled Cheque:</strong>
                            <div className="mt-2">
                              <img
                                src={selectedKyc.bankDetails.cancelledChequeUrl}
                                alt="Cancelled Cheque"
                                style={{
                                  width: "100%",
                                  maxHeight: "250px",
                                  objectFit: "contain",
                                  border: "1px solid #ddd",
                                  borderRadius: "8px",
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  openDocument(
                                    selectedKyc.bankDetails.cancelledChequeUrl,
                                  )
                                }
                              />
                              <div className="mt-2">
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() =>
                                    openDocument(
                                      selectedKyc.bankDetails
                                        .cancelledChequeUrl,
                                    )
                                  }
                                >
                                  View Full Image
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-muted">No bank details provided</p>
                    )}

                    {/* UPI Details Section */}
                    <h6 className="mt-4 mb-3">UPI Details</h6>
                    {selectedKyc.upiDetails && selectedKyc.upiDetails.upiId ? (
                      <p>
                        <strong>UPI ID:</strong> {selectedKyc.upiDetails.upiId}
                      </p>
                    ) : (
                      <p className="text-muted">No UPI details provided</p>
                    )}

                    {selectedKyc.rejectionReason && (
                      <Alert variant="warning" className="mt-3">
                        <strong>Rejection Reason:</strong>
                        <br />
                        {selectedKyc.rejectionReason}
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col md={4}>
                <div className="text-center py-5">
                  <FaShieldAlt size={64} className="text-primary mb-3" />
                  <h5>KYC Verification</h5>
                  <p className="text-muted">KYC verification includes:</p>
                  <ul className="list-unstyled">
                    <li>✓ Bank Account Details OR</li>
                    <li>✓ UPI ID Details</li>
                  </ul>
                  <div className="mt-4">
                    <small className="text-muted">
                      <strong>Note:</strong> Personal documents are managed
                      separately from KYC verification.
                    </small>
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
          {selectedKyc?.status === "pending" && (
            <>
              <Button
                variant="success"
                onClick={() => {
                  setShowDetailModal(false);
                  handleAction(selectedKyc, "approve");
                }}
              >
                <FaCheck className="me-1" />
                Approve KYC
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  setShowDetailModal(false);
                  handleAction(selectedKyc, "reject");
                }}
              >
                <FaTimes className="me-1" />
                Reject KYC
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Action Confirmation Modal */}
      <Modal
        show={showActionModal}
        onHide={() => setShowActionModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {actionType === "approve" ? (
              <>
                <FaCheck className="me-2 text-success" />
                Approve KYC
              </>
            ) : (
              <>
                <FaTimes className="me-2 text-danger" />
                Reject KYC
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to {actionType} the KYC verification for{" "}
            <strong>{selectedKyc?.vendorInfo?.name}</strong>?
          </p>

          {actionType === "reject" && (
            <Form.Group className="mt-3">
              <Form.Label>Rejection Reason *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a detailed reason for rejection..."
                required
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowActionModal(false)}>
            Cancel
          </Button>
          <Button
            variant={actionType === "approve" ? "success" : "danger"}
            onClick={processAction}
            disabled={
              actionLoading ||
              (actionType === "reject" && !rejectionReason.trim())
            }
          >
            {actionLoading ? (
              <Spinner animation="border" size="sm" className="me-2" />
            ) : actionType === "approve" ? (
              <FaCheck className="me-1" />
            ) : (
              <FaTimes className="me-1" />
            )}
            Confirm {actionType === "approve" ? "Approval" : "Rejection"}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .kyc-verification-container {
          background-color: #f8f9fa;
          min-height: 100vh;
        }
        .table th {
          border-top: none;
          font-weight: 600;
          background-color: #f8f9fa;
        }
        .badge {
          font-size: 0.8em;
        }
      `}</style>
    </div>
  );
};

export default KYCVerification;
